import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import ConfirmDialog from "@/components/confirm-dialog";
import { PageHeader } from "@/components/page-header";
import { EntranceForm } from "@/components/site-setup/entrances";
import type { SiteEntrance } from "@/components/site-setup-types";
import { FormErrorAlert } from "@/components/stone/form-errors";
import { getErrorMessage } from "@/lib/form-errors";
import { trpc } from "@/utils/trpc";

export default function SiteEntranceEdit() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id: siteId, entranceId } = useParams<{
    id: string;
    entranceId: string;
  }>();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const site = useQuery({
    ...trpc.getSite.queryOptions({ id: siteId || "" }),
    enabled: Boolean(siteId),
  });

  const updateEntranceMutation = useMutation({
    ...trpc.updateSiteEntrance.mutationOptions(),
    onSuccess: () => {
      setSubmitError(null);
      toast.success("Entrance updated successfully!");
      // Invalidate the site query to refresh the entrances list
      queryClient.invalidateQueries({
        queryKey: trpc.getSite.queryOptions({ id: siteId || "" }).queryKey,
      });
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to update entrance");
      setSubmitError(message);
      toast.error(message);
    },
  });

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const deleteEntranceMutation = useMutation({
    ...trpc.deleteSiteEntrance.mutationOptions(),
    onSuccess: () => {
      toast.success("Entrance deleted successfully!");
      // Invalidate the site query to refresh the entrances list
      queryClient.invalidateQueries({
        queryKey: trpc.getSite.queryOptions({ id: siteId || "" }).queryKey,
      });
      navigate(`/sites/${siteId}`);
    },
    onError: (error) => {
      const message = getErrorMessage(error, "Failed to delete entrance");
      setDeleteError(message);
      toast.error(message);
    },
  });
  const handleDeleteEntrance = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  const handleSaveEntrance = (entranceData: SiteEntrance) => {
    if (!entranceId) return;

    updateEntranceMutation.mutate({
      id: entranceId,
      name: entranceData.name,
      lat: entranceData.lat,
      lon: entranceData.lon,
      plusCode: entranceData.plusCode ?? undefined,
      geofenceDistance: entranceData.geofenceDistance,
      isPrimary: entranceData.isPrimary,
    });
  };

  if (site.isLoading) {
    return <div>Loading...</div>;
  }

  if (site.isError) {
    return (
      <FormErrorAlert
        message={getErrorMessage(site.error, "Failed to load site")}
      />
    );
  }

  if (!site.data) {
    return <FormErrorAlert title="Not found" message="Site not found" />;
  }

  const entrance = site.data.entrances?.find(
    (siteEntrance) => siteEntrance.id === entranceId
  );

  if (!entrance) {
    return <div>Entrance not found</div>;
  }

  return (
    <>
      <PageHeader
        title="Edit Entrance"
        description={`Editing entrance for ${site.data.name}`}
        backTo={`/sites/${siteId}`}
        actions={[
          {
            label: "Delete Entrance",
            onClick: handleDeleteEntrance,
            variant: "destructive",
            disabled: deleteEntranceMutation.isPending,
          },
        ]}
      />

      <EntranceForm
        entrance={entrance}
        siteLat={site.data.lat}
        siteLon={site.data.lon}
        onSave={handleSaveEntrance}
        onCancel={() => navigate(`/sites/${siteId}`)}
        submitError={submitError}
        onClearSubmitError={() => setSubmitError(null)}
      />

      <ConfirmDialog
        open={showDeleteDialog}
        onOpenChange={(open) => {
          setShowDeleteDialog(open);
          if (!open) setDeleteError(null);
        }}
        title="Delete this entrance?"
        description={
          <div>
            <strong>
              This will permanently remove the entrance and its data.
            </strong>
            <ul className="list-disc list-inside mt-2">
              <li>All sign-in records for this entrance will be deleted</li>
              <li>This action cannot be undone</li>
            </ul>
          </div>
        }
        confirmLabel="Yes, delete entrance"
        pendingLabel="Deleting..."
        pending={deleteEntranceMutation.isPending}
        error={deleteError}
        onConfirm={async () => {
          if (!entranceId || !siteId) return;
          await deleteEntranceMutation.mutateAsync({ id: entranceId, siteId });
        }}
      />
    </>
  );
}
